import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [profile, setProfile] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [onboardingState, setOnboardingState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Axios instance with auth header
  const authAxios = axios.create({
    baseURL: API,
  });

  authAxios.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  authAxios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = async () => {
    try {
      const response = await authAxios.get('/auth/me');
      setUser(response.data.user);
      setUserType(response.data.user_type);
      if (response.data.profile) setProfile(response.data.profile);
      if (response.data.preferences) setPreferences(response.data.preferences);
      if (response.data.onboarding_state) setOnboardingState(response.data.onboarding_state);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user_type, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setUserType(user_type);
    return response.data;
  };

  const register = async (email, password, linkedinProfileUrl, referralCode = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      linkedin_profile_url: linkedinProfileUrl,
      referral_code: referralCode,
    });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUserType(null);
    setProfile(null);
    setPreferences(null);
    setOnboardingState(null);
  };

  const verifyEmail = async (verificationToken) => {
    const response = await axios.post(`${API}/auth/verify-email`, { token: verificationToken });
    return response.data;
  };

  const resendVerification = async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, { email });
    return response.data;
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const updateProfile = async (data) => {
    const response = await authAxios.put('/candidates/profile', data);
    await refreshUser();
    return response.data;
  };

  const updatePreferences = async (data) => {
    const response = await authAxios.put('/candidates/preferences', data);
    await refreshUser();
    return response.data;
  };

  const saveOnboardingStep = async (section, step, data) => {
    const response = await authAxios.post('/candidates/onboarding/save-step', {
      section,
      step,
      data,
    });
    await refreshUser();
    return response.data;
  };

  const value = {
    user,
    userType,
    profile,
    preferences,
    onboardingState,
    loading,
    isAuthenticated: !!token && !!user,
    isCandidate: userType === 'candidate',
    isAdmin: userType === 'admin',
    login,
    register,
    logout,
    verifyEmail,
    resendVerification,
    refreshUser,
    updateProfile,
    updatePreferences,
    saveOnboardingStep,
    authAxios,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
